<script setup lang="ts">
import type { BrokerAvatarProps } from '.';

import { AvatarRoot } from 'radix-vue';
import { avatarVariant, companyAvatarVariant } from '.';
import { AvatarFallback, AvatarImage } from '@/components/ui/avatar';

import { cn } from '@/utils';
import { getProvidedImageHandler, getUrlFromStaticDNS } from '~/utils';

const props = withDefaults(defineProps<BrokerAvatarProps>(), {
	avatarFallbackClass: 'bg-white-dark-3 text-base text-white',
	companyAvatarFallbackClass: 'bg-white-dark-2 text-base text-white',
	showCompanyAvatar: true,
	brokerSrc: '/images/fallbacks/agent-avatar.webp',
	companySrc: '/images/fallbacks/company-avatar.webp',
	initials: '',
	brokerName: '',
	companyName: ''
});

const brokerAlt = computed(() => `${props.brokerName} ${props.companyName}`);
const companyAlt = computed(() => `${props.companyName}`);
</script>

<template>
	<div :class="cn('relative', props.class)">
		<AvatarRoot
			:class="
				cn(
					avatarVariant({ shape: avatarShape, border, isGrayscale }),
					props.class
				)
			"
		>
			<AvatarImage :src="brokerSrc || ''" :alt="brokerAlt" />
			<AvatarFallback
				as-child
				:class="
					cn(
						'grid size-full place-items-center',
						avatarFallbackClass,
						props.class
					)
				"
			>
				<NuxtImg
					:src="getUrlFromStaticDNS('/images/fallbacks/agent-avatar.webp')"
					loading="eager"
					format="webp"
					class="size-full rounded-full"
					:provider="getProvidedImageHandler()"
				/>
			</AvatarFallback>
		</AvatarRoot>
		<AvatarRoot
			v-if="showCompanyAvatar"
			:class="cn(companyAvatarVariant({ shape: companyAvatarShape, sizes }))"
		>
			<AvatarImage :src="companySrc || ''" :alt="companyAlt" />

			<AvatarFallback
				as-child
				:class="
					cn(
						'grid min-h-full w-full place-items-center',
						companyAvatarFallbackClass
					)
				"
			>
				<NuxtImg
					:src="getUrlFromStaticDNS('/images/fallbacks/company-avatar.webp')"
					loading="eager"
					format="webp"
					class="size-full rounded-full"
					:provider="getProvidedImageHandler()"
				/>
			</AvatarFallback>
		</AvatarRoot>
	</div>
</template>
